/**
 * WorldState — Singleton holding the current world data and party position.
 * Manages fog of war state and provides utility methods for scene access.
 *
 * @module WorldState
 */

import DebugLog from '../debug/DebugLog.js';

const WorldState = {
  /** @type {object|null} worldData from WorldGenerator */
  world: null,

  /** @type {number|null} Which region the party is currently in */
  currentRegionId: null,

  /** @type {number|null} ID of the current local map (same as regionId for now) */
  currentLocalMapId: null,

  /** @type {{x: number, y: number}} Party position in local map tile coords */
  partyPosition: { x: 0, y: 0 },

  /**
   * Fog of war state.
   * Key: `regionId:x:y`, value: 0=unknown, 1=semi-explored, 2=fully revealed.
   * @type {Map<string, 0|1|2>}
   */
  fog: new Map(),

  /**
   * Cache of generated local map tile grids.
   * Key: regionId, value: { tiles: Array, gridW, gridH }
   * @type {Map<number, object>}
   */
  _localMaps: new Map(),

  // ─── Time ─────────────────────────────────────────────────────────────────

  /**
   * Current in-game time.
   * Managed by TimeSystem; stored here so it persists across scenes and is
   * included in save data.
   * @type {{ day: number, hour: number, minute: number }}
   */
  time: { day: 1, hour: 8, minute: 0 },

  // ─── World context ────────────────────────────────────────────────────────

  /**
   * Biome of the region the party is currently in.
   * Updated when entering a new region on the local map.
   * @type {string}
   */
  currentBiome: 'forest',

  /**
   * Difficulty level of the region the party is currently in (1–10).
   * Updated when entering a new region on the local map.
   * @type {number}
   */
  currentRegionLevel: 1,

  /**
   * Party fame / reputation score (–1000 to 1000).
   * 0 = neutral; positive = renowned; negative = infamous.
   * @type {number}
   */
  partyFame: 0,

  /**
   * Number of active players (1–4).
   * Set during party builder finalization via CoopSystem.setPlayerCount().
   * Stored here so save/load can restore it without importing CoopSystem.
   * @type {number}
   */
  playerCount: 1,

  /**
   * Active quest instances (managed by QuestSystem; mirrored here for save system).
   * @type {Array<object>}
   */
  activeQuests: [],

  /**
   * IDs of completed quests (managed by QuestSystem; mirrored here for save system).
   * @type {string[]}
   */
  completedQuests: [],

  /**
   * Active dungeon state (set by DungeonSystem when a dungeon is entered).
   * Null when not in a dungeon. Included in save snapshots.
   * @type {object|null}
   */
  dungeonState: null,

  /**
   * Initialize WorldState with generated world data.
   * Sets the party starting position in the start region.
   * @param {object} worldData
   */
  init(worldData) {
    this.world = worldData;
    this.fog = new Map();
    this._localMaps = new Map();

    // Start in the designated start region
    this.currentRegionId = worldData.startRegionId;
    this.currentLocalMapId = worldData.startRegionId;

    // Position party at center of local map
    this.partyPosition = { x: 20, y: 15 };

    // Reset time to morning of Day 1
    this.time = { day: 1, hour: 8, minute: 0 };

    // Reset fame and resources
    this.partyFame = 0;
    this.partyGold = 200;
    this.rationPoints = 20;

    // Reset camp equipment
    this.campEquipment = { hasTent: false, hasAlarmWire: false, hasPalisade: false };

    // Reset player count
    this.playerCount = 1;

    // Reset quest state
    this.activeQuests = [];
    this.completedQuests = [];

    // Reset dungeon state
    this.dungeonState = null;

    // Reset town state
    this.currentTown = null;
    this.haggledToday = {};

    // Mark start region as explored and sync biome/level
    const startRegion = worldData.regions.find(r => r.id === worldData.startRegionId);
    if (startRegion) {
      startRegion.explored    = true;
      this.currentBiome       = startRegion.biome  ?? 'forest';
      this.currentRegionLevel = startRegion.level  ?? 1;
    }

    DebugLog.info(
      `[world] WorldState initialized; startRegion=${worldData.startRegionId} ` +
      `regions=${worldData.regions.length} towns=${worldData.towns.length}`
    );
  },

  /**
   * Get fog state at a specific tile in a region.
   * @param {number} regionId
   * @param {number} x
   * @param {number} y
   * @returns {0|1|2} 0=unknown, 1=semi, 2=revealed
   */
  getFogAt(regionId, x, y) {
    return this.fog.get(`${regionId}:${x}:${y}`) ?? 0;
  },

  /**
   * Set fog state at a specific tile in a region.
   * @param {number} regionId
   * @param {number} x
   * @param {number} y
   * @param {0|1|2} state
   */
  setFogAt(regionId, x, y, state) {
    this.fog.set(`${regionId}:${x}:${y}`, state);
  },

  /**
   * Reveal tiles in a radius around a position on the current local map.
   * Sets all tiles within radius to state 2 (fully revealed).
   * Tiles in the ring just outside radius become state 1 (semi).
   * @param {number} cx — Center X tile coord
   * @param {number} cy — Center Y tile coord
   * @param {number} radius — Reveal radius in tiles
   */
  revealAround(cx, cy, radius) {
    if (this.currentRegionId === null) return;

    const regionId = this.currentRegionId;
    const semiRadius = radius + 3;

    for (let dy = -semiRadius; dy <= semiRadius; dy++) {
      for (let dx = -semiRadius; dx <= semiRadius; dx++) {
        const dist = Math.sqrt(dx * dx + dy * dy);
        const tx = cx + dx;
        const ty = cy + dy;

        if (dist <= radius) {
          this.setFogAt(regionId, tx, ty, 2);
        } else if (dist <= semiRadius) {
          // Only set to semi if currently unknown
          const current = this.getFogAt(regionId, tx, ty);
          if (current === 0) {
            this.setFogAt(regionId, tx, ty, 1);
          }
        }
      }
    }
  },

  /**
   * Reveal the entire world — removes all fog.
   * Used by debug console revealmap command.
   */
  revealAll() {
    if (!this.world) return;

    // Mark all regions as explored
    for (const region of this.world.regions) {
      region.explored = true;

      // Reveal all local map cells for this region (use a generous bounds)
      for (const cell of region.cells) {
        this.setFogAt(region.id, cell.x, cell.y, 2);
      }
    }

    // Also reveal the current local map fully
    if (this.currentRegionId !== null) {
      const localMap = this._localMaps.get(this.currentRegionId);
      if (localMap) {
        for (let y = 0; y < localMap.gridH; y++) {
          for (let x = 0; x < localMap.gridW; x++) {
            this.setFogAt(this.currentRegionId, x, y, 2);
          }
        }
      } else {
        // Reveal a large area even without a generated local map
        for (let y = 0; y < 40; y++) {
          for (let x = 0; x < 40; x++) {
            this.setFogAt(this.currentRegionId, x, y, 2);
          }
        }
      }
    }

    DebugLog.info('[world] WorldState.revealAll: all fog removed');
  },

  /**
   * Reset all fog to state 0 (unknown).
   * Used by debug console hidemap command.
   */
  hideAll() {
    this.fog = new Map();

    // Mark all regions as unexplored except start region
    if (this.world) {
      for (const region of this.world.regions) {
        region.explored = region.id === this.world.startRegionId;
      }
    }

    DebugLog.info('[world] WorldState.hideAll: all fog restored');
  },

  /**
   * Store a generated local map for a region so it persists across visits.
   * @param {number} regionId
   * @param {object} localMapData — { tiles, gridW, gridH }
   */
  setLocalMap(regionId, localMapData) {
    this._localMaps.set(regionId, localMapData);
  },

  /**
   * Retrieve a previously generated local map.
   * @param {number} regionId
   * @returns {object|null}
   */
  getLocalMap(regionId) {
    return this._localMaps.get(regionId) ?? null;
  },

  /**
   * Get a region object by ID.
   * @param {number} regionId
   * @returns {object|null}
   */
  getRegion(regionId) {
    if (!this.world) return null;
    return this.world.regions.find(r => r.id === regionId) ?? null;
  },

  /**
   * Mark a region as explored and update the active biome/level context.
   * Called when the player enters a region on the local map.
   * @param {number} regionId
   */
  exploreRegion(regionId) {
    const region = this.getRegion(regionId);
    if (!region) return;

    if (!region.explored) {
      region.explored = true;
      DebugLog.info(`[world] Region explored: ${region.name} (id=${regionId})`);
    }

    // Always sync biome and level when entering any region
    if (region.biome  !== undefined) this.currentBiome       = region.biome;
    if (region.level  !== undefined) this.currentRegionLevel = region.level;
  },

  // ─── Town state ──────────────────────────────────────────────────────────

  /**
   * The current town data object when the party is inside a town.
   * Null when the party is not in a town.
   * @type {object|null}
   */
  currentTown: null,

  /**
   * Tracks which shop types the party has already haggled at today.
   * Map of shopType → boolean. Reset at the start of each in-game day.
   * @type {Object<string, boolean>}
   */
  haggledToday: {},

  /**
   * Enter a town. Sets currentTown and logs the transition.
   * @param {object} townData — Generated town instance from TownSystem.generateTown()
   */
  enterTown(townData) {
    this.currentTown = townData;
    DebugLog.info(`[world] Entered town: "${townData?.name}" (type=${townData?.townType})`);
  },

  /**
   * Exit the current town. Clears currentTown and resets haggledToday.
   */
  exitTown() {
    const townName = this.currentTown?.name ?? 'unknown';
    this.currentTown = null;
    this.haggledToday = {};
    DebugLog.info(`[world] Exited town: "${townName}"`);
  },

  // ─── Economy & resources ──────────────────────────────────────────────────

  /** @type {number} Shared party gold */
  partyGold: 200,

  /** @type {number} Shared party ration points (used for camping/travel) */
  rationPoints: 20,

  /**
   * Camp fortification equipment owned by the party.
   * Used by CampingSystem to reduce night encounter chance.
   * @type {{ hasTent: boolean, hasAlarmWire: boolean, hasPalisade: boolean }}
   */
  campEquipment: { hasTent: false, hasAlarmWire: false, hasPalisade: false },

  /**
   * Compute party encumbrance across all characters.
   * Max carry weight = 10 + Might×3 per character, summed for party.
   * @param {object[]} party — Array of character objects
   * @returns {{ current: number, max: number, overweight: boolean, penalty: number }}
   */
  getPartyEncumbrance(party) {
    if (!party || party.length === 0) {
      return { current: 0, max: 10, overweight: false, penalty: 0 };
    }

    let totalCurrent = 0;
    let totalMax = 0;

    for (const character of party) {
      const might = character.attributes?.might ?? 10;
      const charMax = 10 + might * 3;

      // Belt carryBonus from equipment
      const beltItem = character.equipment?.belt;
      const beltBonus = beltItem
        ? (beltItem.stats?.carryBonus ?? 0)
        : 0;

      totalMax += charMax + beltBonus;

      // Sum inventory weights
      const inventory = character.inventory ?? [];
      for (const item of inventory) {
        const weight = item.weight ?? 0;
        const qty = item.quantity ?? 1;
        totalCurrent += weight * qty;
      }

      // Sum equipped item weights
      const equipment = character.equipment ?? {};
      for (const slot of Object.values(equipment)) {
        if (slot && slot.weight != null) {
          totalCurrent += slot.weight;
        }
      }
    }

    const overweight = totalCurrent > totalMax;
    // Penalty: 0–50% speed reduction, proportional to how overweight
    let penalty = 0;
    if (overweight) {
      const excess = totalCurrent - totalMax;
      penalty = Math.min(0.5, excess / totalMax);
    }

    return {
      current: Math.round(totalCurrent * 10) / 10,
      max: Math.round(totalMax * 10) / 10,
      overweight,
      penalty: Math.round(penalty * 100),
    };
  },
};

export default WorldState;
